import { create } from 'zustand';
import { feedbackApi } from '../services/api';
import { getAssignmentStatus } from '../constants/assignmentStatuses';

// Central initial state for easy reset
const initialState = {
  assignments: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  // Filters
  selectedStatuses: [],
  selectedReviewTypes: [],
  selectedEmployee: null,
  // UI State
  isReturningFromDetails: false,
  showControls: true,
};

const useIndividualAssignmentsStore = create((set, get) => {
  // Fetch all assignments from API
  const fetchAssignments = async () => {
    if (get().isLoading) {
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await feedbackApi.getEmployeesIndividualAssignments();

      if (response) {
        set({
          assignments: response,
          pageInfo: {
            endCursor: null,
            hasNextPage: false,
            totalCount: response.length
          },
          isLoading: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false });
      return null;
    } catch (err) {
      set({
        error: err.message || 'Failed to load individual assignments',
        isLoading: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setShowControls: (show) => set({ showControls: show }),

    // Filter setters
    setSelectedStatuses: (statuses) => set({ selectedStatuses: statuses }),
    setSelectedReviewTypes: (reviewTypes) => set({ selectedReviewTypes: reviewTypes }),
    setSelectedEmployee: (employee) => set({ selectedEmployee: employee }),

    // Toggle handlers for chip-based filters
    toggleStatus: (status) => {
      const { selectedStatuses } = get();
      if (selectedStatuses.includes(status)) {
        set({ selectedStatuses: selectedStatuses.filter(s => s !== status) });
      } else {
        set({ selectedStatuses: [...selectedStatuses, status] });
      }
    },

    toggleReviewType: (reviewType) => {
      const { selectedReviewTypes } = get();
      if (selectedReviewTypes.includes(reviewType)) {
        set({ selectedReviewTypes: selectedReviewTypes.filter(rt => rt !== reviewType) });
      } else {
        set({ selectedReviewTypes: [...selectedReviewTypes, reviewType] });
      }
    },

    // Data actions
    fetchAssignmentsWithReset: () => fetchAssignments(),

    // No-op since API doesn't support pagination
    fetchAssignmentsLoadMore: () => Promise.resolve(null),

    // Update an assignment in the store
    updateAssignment: (updatedAssignment) => {
      if (!updatedAssignment || !updatedAssignment.id) {
        return false;
      }
      const { assignments, pageInfo } = get();
      const index = assignments.findIndex(a => a.id === updatedAssignment.id);

      if (index !== -1) {
        const updatedAssignments = [...assignments];
        updatedAssignments[index] = { ...updatedAssignments[index], ...updatedAssignment };
        set({ assignments: updatedAssignments });
        return true;
      } else {
        // Add new assignment
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1
        };
        set({
          assignments: [updatedAssignment, ...assignments],
          pageInfo: updatedPageInfo
        });
        return true;
      }
    },

    // Remove an assignment from the store
    removeAssignment: (assignmentId) => {
      if (!assignmentId) {
        return false;
      }

      const { assignments, pageInfo } = get();
      const index = assignments.findIndex(a => a.id === assignmentId);

      if (index !== -1) {
        const updatedAssignments = assignments.filter(a => a.id !== assignmentId);
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: Math.max(0, pageInfo.totalCount - 1)
        };
        set({
          assignments: updatedAssignments,
          pageInfo: updatedPageInfo
        });
        return true;
      }
      return false;
    },

    // Filters reset
    resetFilters: () => {
      set({
        selectedStatuses: [],
        selectedReviewTypes: [],
        selectedEmployee: null,
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
    }),

    // Computed: get filtered assignments
    getFilteredAssignments: () => {
      const { assignments, selectedStatuses, selectedReviewTypes, selectedEmployee } = get();

      return assignments.filter(assignment => {
        // Status filter (computed from assignment fields)
        const status = getAssignmentStatus(assignment);
        const matchesStatus = selectedStatuses.length === 0 ||
          selectedStatuses.includes(status);

        // Review type filter (from reviewers array)
        const reviewType = assignment.reviewers?.[0]?.reviewType;
        let matchesReviewType = true;
        if (selectedReviewTypes.length > 0) {
          if (reviewType) {
            matchesReviewType = selectedReviewTypes.includes(reviewType);
          } else {
            // If no review type found, don't show when filters are active
            matchesReviewType = false;
          }
        }

        // Employee filter
        const matchesEmployee = !selectedEmployee ||
          assignment.employee?.id === selectedEmployee.id;

        return matchesStatus && matchesReviewType && matchesEmployee;
      });
    },
  };
});

export default useIndividualAssignmentsStore;
